import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AdminLayout, { AdminSection } from '../../components/AdminLayout';
import axiosInstance from '../../utils/axiosInstance';

const initialForm = {
  name: '',
  code: '',
  description: '',
};

function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function statusColor(status) {
  if (status === 'active') {
    return 'success';
  }
  if (status === 'draft') {
    return 'warning';
  }
  if (status === 'retired' || status === 'deprecated') {
    return 'default';
  }
  return 'primary';
}

export default function AdminNeedCategoriesPage(props) {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: '', categories: [] });
  const [form, setForm] = useState(initialForm);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');

  const activeCount = useMemo(
    () => state.categories.filter((category) => category.status === 'active').length,
    [state.categories],
  );

  async function loadData() {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get('/admin/need-categories');
      setState({
        loading: false,
        error: '',
        categories: Array.isArray(response.data?.need_categories) ? response.data.need_categories : [],
      });
    } catch (error) {
      setState((current) => ({
        ...current,
        loading: false,
        error: error?.response?.data?.message || 'Unable to load need categories.',
      }));
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  function updateForm(patch) {
    setForm((current) => {
      const next = { ...current, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, 'name') && !current.code) {
        next.code = slugify(patch.name);
      }
      return next;
    });
  }

  async function handleCreate(event) {
    event.preventDefault();
    setSaving(true);
    setFeedback('');
    setState((current) => ({ ...current, error: '' }));
    try {
      const response = await axiosInstance.post('/admin/need-categories', {
        name: form.name,
        code: form.code || slugify(form.name),
        description: form.description,
      });
      const categoryId = response.data?.need_category?.category_id;
      setForm(initialForm);
      setDialogOpen(false);
      setFeedback('Need category draft created.');
      if (categoryId) {
        navigate(`/admin/need-categories/${encodeURIComponent(categoryId)}`);
      } else {
        await loadData();
      }
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error?.response?.data?.message || 'Unable to create need category.',
      }));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout
      props={props}
      breadcrumbItems={[{ label: 'AMX xConsole', href: '/' }, { label: 'Admin Dashboard', href: '/admin' }, { label: 'Need Categories' }]}
      title="Need Categories"
      subtitle="Define the standardized types of needs exchanged through AMX."
    >
      {feedback ? <Alert severity="success">{feedback}</Alert> : null}
      {state.error ? <Alert severity="error">{state.error}</Alert> : null}

      <AdminSection
        title="Federation Taxonomy"
        subtitle={`${activeCount} active of ${state.categories.length} total categories.`}
        action={(
          <Button variant="contained" startIcon={<AddCircleRoundedIcon />} onClick={() => setDialogOpen(true)}>
            Create Need Category
          </Button>
        )}
      >
        {state.loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <span>Loading need categories...</span>
          </Stack>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Need Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Current Version</TableCell>
                <TableCell>Fields</TableCell>
                <TableCell>Matching Fields</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.categories.map((category) => (
                <TableRow key={category.category_id} hover>
                  <TableCell>
                    <Box sx={{ maxWidth: 560 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {category.category_display_name}
                      </Typography>
                      {category.description ? (
                        <Typography variant="caption" color="text.secondary">
                          {category.description}
                        </Typography>
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={statusColor(category.status)} label={category.status || 'draft'} />
                  </TableCell>
                  <TableCell>{category.current_schema_version || category.current_version || '-'}</TableCell>
                  <TableCell>{category.field_count ?? 0}</TableCell>
                  <TableCell>{category.matchable_field_count ?? 0}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditRoundedIcon />}
                      onClick={() => navigate(`/admin/need-categories/${encodeURIComponent(category.category_id)}`)}
                    >
                      Manage
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!state.categories.length ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      No need categories have been created yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        )}
      </AdminSection>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Need Category</DialogTitle>
        <DialogContent>
          <Stack component="form" id="need-category-create-form" spacing={2} onSubmit={handleCreate} sx={{ pt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(event) => updateForm({ name: event.target.value })}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Code"
              value={form.code}
              onChange={(event) => updateForm({ code: slugify(event.target.value) })}
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(event) => updateForm({ description: event.target.value })}
              fullWidth
              multiline
              minRows={3}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button type="submit" form="need-category-create-form" variant="contained" disabled={saving || !form.name.trim() || !form.description.trim()}>
            {saving ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
}
